import React, { useEffect, useRef } from 'react';
import { Input, Select, Tooltip, Button, Switch } from 'antd';
import {
  SearchOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { PTF_RETURN_ANALYZED } from 'state';

import type { Studio2Player } from '../live/types';
import { pct, signClass, BAND_COLORS } from '../helpers';
import BandChip from './BandChip';

export type RailSortKey = 'rank' | 'return' | 'sharpe' | 'mktval' | 'name';

type Props = {
  players: Studio2Player[];
  selectedId: number;
  onSelect: (id: number) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  search: string;
  onSearch: (v: string) => void;
  sortKey: RailSortKey;
  onSortKey: (k: RailSortKey) => void;
  showActiveOnly: boolean;
  onToggleActive: (v: boolean) => void;
};

const SORT_OPTIONS = [
  { value: 'return', label: 'Return' },
  { value: 'rank', label: 'Rank' },
  // Temporarily disabled — these sorts aren't wired up yet. Keep for later.
  // { value: 'sharpe', label: 'Sharpe' },
  // { value: 'mktval', label: 'Market value' },
  // { value: 'name', label: 'Name' },
];

const overallBand = (p: Studio2Player): number | null => {
  const m = p.analysis.analyzed_metrics.find(
    a => a.analyzed_metric === PTF_RETURN_ANALYZED,
  );
  return m ? m.band : null;
};

const initials = (name: string) =>
  name
    .split(' ')
    .map(s => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const Rail: React.FC<Props> = ({
  players,
  selectedId,
  onSelect,
  collapsed,
  onToggleCollapsed,
  search,
  onSearch,
  sortKey,
  onSortKey,
  showActiveOnly,
  onToggleActive,
}) => {
  // Keep the selected row visible when navigating by keyboard or sorting.
  const activeRowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  return (
    <div
      className={`studio2__rail ${collapsed ? 'studio2__rail--collapsed' : ''}`}
    >
      <div className="studio2__rail-head">
        {!collapsed && <span className="studio2__rail-title">Roster</span>}
        <Tooltip title={collapsed ? 'Expand roster' : 'Collapse roster'}>
          <Button
            type="text"
            size="small"
            aria-label={collapsed ? 'Expand roster' : 'Collapse roster'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={onToggleCollapsed}
          />
        </Tooltip>
      </div>

      {!collapsed && (
        <div className="studio2__rail-toolbar">
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="Search players"
            value={search}
            onChange={e => onSearch(e.target.value)}
          />
          <Select
            size="small"
            value={sortKey}
            onChange={onSortKey}
            options={SORT_OPTIONS}
            style={{ width: '100%' }}
          />
          <Tooltip title="Show only active accounts (those that have placed at least one deal)">
            <label className="studio2__rail-toggle">
              <span className="studio2__rail-toggle-label">Active only</span>
              <Switch
                size="small"
                checked={showActiveOnly}
                onChange={onToggleActive}
              />
            </label>
          </Tooltip>
        </div>
      )}

      <div className="studio2__rail-list">
        {players.map(p => {
          const active = p.id === selectedId;
          const ret = p.last_portfolio_return;
          if (collapsed) {
            return (
              <Tooltip
                key={p.id}
                placement="right"
                title={`${p.name} · ${pct(ret)}`}
              >
                <div
                  ref={active ? activeRowRef : undefined}
                  className={`studio2__rail-row studio2__rail-row--mini ${
                    active ? 'studio2__rail-row--active' : ''
                  }`}
                  onClick={() => onSelect(p.id)}
                >
                  <span
                    className={`studio2__dot ${p.online ? 'studio2__dot--online' : ''}`}
                  />
                  <span className="studio2__rail-initials">
                    {initials(p.name)}
                  </span>
                  <div className="studio2__rail-barbg">
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(100, Math.abs(ret) * 400)}%`,
                        background: ret >= 0 ? BAND_COLORS[0] : BAND_COLORS[3],
                      }}
                    />
                  </div>
                </div>
              </Tooltip>
            );
          }
          return (
            <div
              key={p.id}
              ref={active ? activeRowRef : undefined}
              className={`studio2__rail-row ${active ? 'studio2__rail-row--active' : ''}`}
              onClick={() => onSelect(p.id)}
            >
              <span
                className={`studio2__dot ${p.online ? 'studio2__dot--online' : ''}`}
              />
              <div className="studio2__rail-id">
                <div style={{ minWidth: 0 }}>
                  <div className="studio2__rail-name">{p.name}</div>
                  <div className="studio2__rail-sub">
                    #{p.report.returns.rank} · {p.online ? 'online' : 'offline'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`studio2__rail-metric ${signClass(ret)}`}>
                  {pct(ret)}
                </span>
                <BandChip band={overallBand(p)} />
              </div>
            </div>
          );
        })}
      </div>

      {!collapsed && (
        <div className="studio2__rail-foot">
          <span className="studio2__kbdhint">
            <span className="studio2__kbd">↑</span>
            <span className="studio2__kbd">↓</span>
            move between players
          </span>
        </div>
      )}
    </div>
  );
};

export default Rail;
